import moment from "moment-timezone";
import { t } from "ttag";

export const getFirmwareVersionId = (version: any): string => {
  const id = version?.id ?? version?._id;
  return id != null && id !== "" ? String(id) : "";
};

export const formatFirmwareDate = (value: any) => {
  if (!value) {
    return "-";
  }
  const asNumber = Number(value);
  const date =
    Number.isFinite(asNumber) && asNumber > 100000
      ? moment(asNumber)
      : moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : String(value);
};

export const describeActionType = (type: string) => {
  const normalized = String(type || "").toLowerCase();
  if (normalized === "update") {
    return "Firmware update assignment";
  }
  if (normalized === "cancelaction") {
    return "Cancel update";
  }
  return type || "-";
};

export const describeActionStatus = (status: string) => {
  const normalized = String(status || "").toLowerCase();
  const map: Record<string, string> = {
    finished: "Finished",
    pending: "Waiting for device",
    running: "In progress on device",
    error: "Failed",
    canceled: "Canceled",
    cancelled: "Canceled",
    retrieved: "Retrieved by device",
    download: "Downloading",
    downloaded: "Downloaded",
    warning: "Warning reported by device",
    scheduled: "Scheduled",
    canceling: "Canceling",
    cancel_rejected: "Cancel rejected",
    wait_for_confirmation: "Waiting for confirmation",
    in_sync: "Up to date",
    registered: "Registered",
    unknown: "Unknown",
    updating: "Updating",
  };
  return map[normalized] || status || "-";
};

export const formatUpgradeVersionLabel = (
  info: { version?: string; isStable?: boolean } | null | undefined
): string => {
  if (!info?.version) {
    return "";
  }
  return info.isStable ? `${info.version} (${t`stable`})` : info.version;
};

// Hawkbit marks the target "error" when the device reports a failed update;
// the action itself is then reported as finished.
export const isFirmwareUpdateFailed = (
  ...statuses: Array<string | undefined>
): boolean => statuses.some((status) => String(status || "").toUpperCase() === "ERROR");

const TERMINAL_FAILURE_STAGES = new Set(["error", "canceled", "cancelled"]);

// The backend holds percent at 99 once the estimate has elapsed but the device
// has not reported "finished" yet (typically its reboot), so 99+ means overrun.
// On failure the backend stops sending progress and the last values linger, so
// a failed/canceled stage is shown on its own rather than beside a countdown.
export const formatFirmwareProgressLabel = ({
  completed,
  percent,
  secondsRemaining,
  stage,
}: {
  completed: boolean;
  percent: number;
  secondsRemaining: number;
  stage?: string;
}): string => {
  if (completed) {
    return t`Completed successfully`;
  }
  if (TERMINAL_FAILURE_STAGES.has(String(stage || "").toLowerCase())) {
    return describeActionStatus(stage as string);
  }
  const status = stage ? ` (${describeActionStatus(stage)})` : "";
  if (percent >= 99) {
    return `${t`Finishing up…`}${status}`;
  }
  return `${t`Estimated time left:`} ${secondsRemaining} ${t`seconds`}${status}`;
};

export const normalizeFirmwareActionDetail = (payload: any) => {
  if (payload?.summary || payload?.action) {
    return {
      action: payload.action || {},
      summary: payload.summary || {},
      statusHistory: Array.isArray(payload.statusHistory)
        ? payload.statusHistory
        : [],
      distributionSet: payload.distributionSet || null,
      catalogFirmware: payload.catalogFirmware || null,
    };
  }

  return {
    action: payload || {},
    summary: {
      actionId: payload?.id,
      purpose: "Firmware update assignment to this device",
      status: payload?.status,
      type: payload?.type,
      forceType: payload?.forceType,
      createdAt: payload?.createdAt,
      lastModifiedAt: payload?.lastModifiedAt,
    },
    statusHistory: [],
    distributionSet: null,
    catalogFirmware: null,
  };
};
